import React, { useEffect, useState, useMemo, useRef, useContext } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import MGrid, { ACTION_ADD, MGridColumnDefinition } from "components/Shared/MGrid";
import { DATA_TYPE_STRING, DATA_TYPE_BUTTON } from "typeConstants/dataTypes";
import responseHandler from "responseHandler";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import { getSettings, deleteSettings, addUpdateSettings } from "requests/customerCalcSettings";
import { CUSTOMER_CALC_SETTINGS_EDIT } from "typeConstants/rights";
import { userHasRight } from "selectors/rights"; 
import { 
  CustomerCalcSettingsDto, 
  Customer360ValueSettingsDto, 
  CustomerRCTAccountSettingsDto, 
  CustomerRCTCalculationSettingsDto 
} from "interfaces/Mueller.Business.Models";
import { CalcToolCodes } from "../../../typeConstants/calcTypeCodes";
import Customer360ValueSettingsForm from "./Customer360ValueSettingsForm";
import CustomerRCTSettingsCombined from "./CustomerRCTSettingsCombined";
import CustomerCalcSettingsWizard from "./CustomerCalcSettingsWizard";

interface CustomerCalcSettingsGridProps {
  customerNumber: number;
  verticalDisplay?: boolean;
  // Redux
  canEdit: boolean;
  makeSuccessToast: (text: string) => void;
}

const CustomerCalcSettingsGrid = (props: CustomerCalcSettingsGridProps) => {
  const [calcSettings, setCalcSettings] = useState<CustomerCalcSettingsDto>({ accountSettings: [], calculationSettings: null });
  const [isLoading, setIsLoading] = useState(false);
  const dialog = useContext(DialogContext);
  const mounted = useRef(true);
  const cellWidth = props.verticalDisplay ? 12 : 6;

  const getCalcSettings = () => {
    const handleSettings = (response: CustomerCalcSettingsDto) => {
      setCalcSettings(response);
      setIsLoading(false);
    };
    getSettings(props.customerNumber, handleSettings, mounted);
  }

  useEffect(() => {
    if (props.customerNumber) {
      setIsLoading(true);
      getCalcSettings();
    }
  }, [props.customerNumber]);

  useEffect(() => {
    return () => { mounted.current = false; }
  }, []);

  // Dialogs

  const hideDialog = () => dialog.hide();
  const dialogClassName = "calc-settings-dialog";
  const dialogProps = { className: dialogClassName};
  const portalTargetClass = `.${dialogClassName}`;

  const onSave360Value = (settings: Customer360ValueSettingsDto) => {
    const payload: CustomerCalcSettingsDto = {
      accountSettings: [{
        name: "", // Not used for add/update
        code: CalcToolCodes.Calc360Value,
        settings360Value: settings,
        settingsRCT: null,
      }],
      calculationSettings: null,
    }
    dialog.hide();
    setIsLoading(true);
    const onSuccess = () => {
      props.makeSuccessToast("Customer 360 Value account settings saved");
      getCalcSettings();
    };
    addUpdateSettings(props.customerNumber, CalcToolCodes.Calc360Value, payload, onSuccess, getCalcSettings, mounted);
  }

  const onSaveRCT = (settings: CustomerRCTAccountSettingsDto,  calcSettings: CustomerRCTCalculationSettingsDto) => {
    const payload: CustomerCalcSettingsDto = {
      accountSettings: [{
        name: "", // Not used for add/update
        code: settings.calcToolCode,
        settings360Value: null,
        settingsRCT: settings,
      }],
      calculationSettings: calcSettings,
    };
    dialog.hide();
    setIsLoading(true);
    const onSuccess = () => {
      props.makeSuccessToast("Customer RCT settings saved");
      getCalcSettings();
    };
    addUpdateSettings(props.customerNumber, settings.calcToolCode, payload, onSuccess, getCalcSettings, mounted);
  };
  
  const show360Dialog = (settings: Customer360ValueSettingsDto) => {
    dialog.show(
      "360 Value Account Settings", 
      <Customer360ValueSettingsForm
        customerNumber={props.customerNumber}
        settings={settings}
        onSave={onSave360Value}
        onCancel={hideDialog}
        canEdit={props.canEdit}
        portalTargetClass={portalTargetClass}
      />, 
      null, 
      dialogProps
    );
  };

  const showRCTDialog = (settings: CustomerRCTAccountSettingsDto) => {
    dialog.show(
      `RCT Settings`, 
      <CustomerRCTSettingsCombined
        customerNumber={props.customerNumber}
        accountSettings={settings}
        calcSettings={calcSettings.calculationSettings}
        onSave={onSaveRCT}
        onCancel={hideDialog}
        canEdit={props.canEdit}
        portalTargetClass={portalTargetClass}
      />, 
      null, 
      dialogProps
    );
  };

  // Grid

  const EDIT_COLUMN_KEY = "EDIT_COLUMN_KEY";
  const DELETE_COLUMN_KEY = "DELETE_COLUMN_KEY";

  const onAdd = () => {
    dialog.show(
      "Copy Calc Settings",
      <CustomerCalcSettingsWizard
        customerNumber={props.customerNumber}
        onAdd360Value={onSave360Value}
        onAddRCT={onSaveRCT}
        onCancel={dialog.hide}
        portalTargetClass={portalTargetClass}
      />,
      null, 
      dialogProps
    );
  };
  const handleGridButtons = (selectedOriginalIndex: number, columnKey: string) => {
    if (columnKey === EDIT_COLUMN_KEY && props.canEdit) {
      const settings = calcSettings.accountSettings[selectedOriginalIndex];
      switch(settings.code) {
        case CalcToolCodes.Calc360Value:
          show360Dialog(settings.settings360Value);
          break;
        default:
          showRCTDialog(settings.settingsRCT);
          break;
      }
    }
    if (columnKey === DELETE_COLUMN_KEY && props.canEdit) {
      const onConfirmDelete = (index: number) => {
        setIsLoading(true);
        const onSuccess = () => {
          props.makeSuccessToast("Customer calc settings deleted");
          getCalcSettings();
        };
        deleteSettings(props.customerNumber, calcSettings.accountSettings[selectedOriginalIndex].code, onSuccess, getCalcSettings, mounted);
      };
      dialog.showYesNo(
        "Confirm Delete Settings",
        `Do you want to delete the ${calcSettings.accountSettings[selectedOriginalIndex].name} settings for this customer? This action cannot be undone.`,
        () => onConfirmDelete(selectedOriginalIndex),
        dialog.hide,
        "Delete",
        "Cancel");
    }
  };

  const memoizedColumns = useMemo(() => {
    const columns: MGridColumnDefinition[] = [
      { key: "calcType", name: "Calc Type", dataSource: "name", width: 200, dataType: DATA_TYPE_STRING },
    ];
    if (props.canEdit) {
      const editButton = () => <i className="md-icon material-icons">edit</i>;
      const deleteButton = () => <i className="md-icon material-icons">delete</i>;
      const actionColumns = [
        {
          key: EDIT_COLUMN_KEY,
          name: "Edit",
          width: 60,
          dataSource: editButton,
          dataType: DATA_TYPE_BUTTON,
          resizable: false,
          tooltip: "Click to edit settings",
        },
        {
          key: DELETE_COLUMN_KEY,
          name: "Delete",
          width: 60,
          dataSource: deleteButton,
          dataType: DATA_TYPE_BUTTON,
          resizable: false,
          tooltip: "Click to delete settings",
        },
      ];
      columns.unshift(...actionColumns);
    }
    return columns;
  }, [props.canEdit]);

  // Render

  return (
    <div className={`customer-calc-settings-grid-container md-paper--1 md-cell--${cellWidth}`}>
      <MGrid
        configurationId="CustomerCalcSettingsGrid"
        headerText="Calc Settings"
        columnDefinitions={memoizedColumns}
        data={calcSettings.accountSettings}
        isFetchingData={isLoading}
        onCellButtonClick={handleGridButtons}
        displayNumOfItemsFound={true}
        actions={ [{ code: ACTION_ADD, onClick: onAdd }]}
        cardStyle
        disableFilters
      />
    </div>);
};

const mapStateToProps = state => ({
  canEdit: userHasRight(CUSTOMER_CALC_SETTINGS_EDIT)(state),
});

const mapDispatchToProps = dispatch => ({
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerCalcSettingsGrid));